import { Light } from '../../types/light';
import { Mesh } from '../../types/mesh';
import { Norm } from '../../types/user';
import { pointInPolygon } from './cadUtils';
import { modelToCanvas, polygonsToMeshes } from './drawingDocument';
import { CadPolygon } from './types';

/** What changed between the drawing the model was built from and the one being edited. */
interface PlanDiff {
  added: CadPolygon[];
  changed: CadPolygon[];
  /** Polygons of the applied drawing that are gone. */
  deleted: CadPolygon[];
  /** Height change per polygon id (new minus old), for polygons whose height changed. */
  heightChanges: Map<string, number>;
}

const geometryKey = (p: CadPolygon) => JSON.stringify([p.points, p.holes ?? [], p.height]);
const attributesKey = (p: CadPolygon) =>
  JSON.stringify([p.name, p.type, p.norm, p.illuminated, p.canHoldLight, p.desiredLuminaireHeight, p.color]);

export function diffDrawings(applied: CadPolygon[], current: CadPolygon[]): PlanDiff {
  const before = new Map(applied.map((p) => [p.id, p]));
  const now = new Map(current.map((p) => [p.id, p]));
  const diff: PlanDiff = { added: [], changed: [], deleted: [], heightChanges: new Map() };
  for (const polygon of current) {
    const old = before.get(polygon.id);
    if (!old) {
      diff.added.push(polygon);
      continue;
    }
    if (geometryKey(old) !== geometryKey(polygon) || attributesKey(old) !== attributesKey(polygon)) {
      diff.changed.push(polygon);
    }
    if (old.height !== polygon.height) diff.heightChanges.set(polygon.id, polygon.height - old.height);
  }
  for (const polygon of applied) {
    if (!now.has(polygon.id)) diff.deleted.push(polygon);
  }
  return diff;
}

export const hasChanges = (diff: PlanDiff) =>
  diff.added.length > 0 || diff.changed.length > 0 || diff.deleted.length > 0;

/** Luminaires affected by applying the drawing. */
interface LampPlan {
  /** Stood on the applied drawing and stand on no polygon of the edited one (deleted or reshaped). */
  remove: Light[];
  /** Stand on a polygon whose height changed; dz moves them with its surface. */
  lift: { light: Light; dz: number }[];
}

const containing = (polygons: CadPolygon[], [x, y]: [number, number]) =>
  polygons.find((p) => p.points.length >= 3 && pointInPolygon(x, y, p.points));

export function planLamps(
  lights: Light[],
  applied: CadPolygon[],
  current: CadPolygon[],
  metersPerUnit: number,
  origin: [number, number],
): LampPlan {
  const heightChanges = diffDrawings(applied, current).heightChanges;
  const plan: LampPlan = { remove: [], lift: [] };
  for (const light of lights) {
    const point = modelToCanvas([light.positionX ?? 0, light.positionY ?? 0], metersPerUnit, origin);
    const under = containing(current, point);
    if (!under) {
      if (containing(applied, point)) plan.remove.push(light);
      continue;
    }
    const dz = heightChanges.get(under.id);
    if (dz) plan.lift.push({ light, dz });
  }
  return plan;
}

/** The luminaires that go with the applied drawing, whole (IES file, lumen output, catalogue product). */
export function lightsForApply(lights: Light[], plan: LampPlan, removeLamps: boolean): Light[] {
  const removed = new Set(removeLamps ? plan.remove.map((light) => light.uuid) : []);
  const lift = new Map(plan.lift.map(({ light, dz }) => [light.uuid, dz]));
  return lights
    .filter((light) => !removed.has(light.uuid))
    .map((light) => ({ ...light, positionZ: (light.positionZ ?? 0) + (lift.get(light.uuid) ?? 0) }));
}

/**
 * A version's layers for a model rebuilt from its drawing. Names, types and heights come from the
 * drawing; the lighting set up in the workspace (norm, luminaire rules, groups) stays with each
 * area (matched by polygon id). A name or type the workspace changed stays too, unless the drawing
 * changed it since the model was built. New areas start unlit.
 */
export function meshesForVersion(
  polygons: CadPolygon[],
  meshNames: string[],
  before: CadPolygon[],
  meshes: Mesh[],
  norms: Norm[],
): Mesh[] {
  const byUuid = new Map(meshes.map((mesh) => [mesh.uuid, mesh]));
  const beforeById = new Map(before.map((p) => [p.id, p]));
  return polygonsToMeshes(polygons, meshNames).map((mesh, index) => {
    const current = byUuid.get(mesh.uuid);
    if (!current) return { ...mesh, normName: undefined, isIlluminated: false, canHoldLight: false };
    const polygon = polygons[index];
    const old = beforeById.get(polygon.id);
    const renamed = !old || old.name !== polygon.name;
    const retyped = !old || old.type !== polygon.type;
    return {
      ...mesh,
      groupId: current.groupId,
      displayName: renamed ? mesh.displayName : current.displayName,
      type: retyped ? mesh.type : current.type,
      color: retyped ? mesh.color : current.color,
      normName: current.targets?.length
        ? undefined
        : current.normId != null
          ? norms.find((n) => n.id === current.normId)?.name
          : undefined,
      targets: current.targets?.length ? current.targets : undefined,
      isIlluminated: current.isIlluminated ?? (current.normId != null || (current.targets?.length ?? 0) > 0),
      canHoldLight: current.canHoldLight,
      desiredLuminaireHeight: current.desiredLuminaireHeight ?? mesh.desiredLuminaireHeight,
    };
  });
}
